import { useListingStepNavigation } from '@/lib/hooks/use-listing-step-navigation'
/**
 * New Listing — Documents (Tarjeta de Circulación)
 *
 * User takes photo → AI extracts data in ~3s → form auto-fills → user confirms.
 * Cross-validates with details step (plate, VIN, make, model).
 */
import { useState, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { Platform, View, Text, ActivityIndicator, ScrollView } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { notification, selection, NotificationType } from '@/lib/utils/haptics'
import { daysFromTodayISO, formatSvDateDisplay, todayISO } from '@/lib/utils/dates'
import Animated from 'react-native-reanimated'
import {
  Camera,
  AlertTriangle,
  CheckCircle,
  SkipForward,
  Building2,
  Scan,
  FileText,
  ShieldCheck,
} from '@/components/ui/icons'
import { Image as ExpoImage } from 'expo-image'
import { Pressable } from '@/components/ui/Pressable'
import { Button } from '@/components/ui/Button'
import { tintIconNode } from '@/components/ui/tint-icon-node'
import { TextInput } from '@/components/ui/TextInput'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { DatePicker } from '@/components/ui/DatePicker'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { FormActionBar } from '@/components/ui/FormActionBar'
import { LiveCameraModal, type LiveCameraCapture } from '@/components/camera/LiveCameraModal'
import { useListingWizardStore } from '@/lib/stores/listing-wizard.store'
import { useProfile } from '@/lib/hooks/use-profile'
import { normalizeVehicleVin, VEHICLE_VIN_LENGTH } from '@/lib/utils/vehicle-document-policy'
import { useExtractInsurancePolicy, useExtractVehicleCard } from '@/lib/hooks/use-vehicle-card'
import { useMakes, useModels } from '@/lib/hooks/use-host-vehicles'
import { CompanyDetectedSheet } from '@/components/hosting/CompanyDetectedSheet'
import { captureError } from '@/lib/utils/observability'
import { JoinOrgSheet } from '@/components/hosting/JoinOrgSheet'
import { InsurerBadge } from '@/components/insurance/InsurerBadge'
import { scanDocumentOutcome } from '@/components/hosting/DocumentScanner'
import { persistListingCapture } from '@/lib/utils/listing-photo-storage'
import { vehicleDocumentSchema } from '@/lib/validations/vehicle-document.schema'
import type { VehicleDetailsInput } from '@/lib/validations/vehicle.schema'
import {
  useSemanticColors,
  SPACING,
  RADIUS,
  INTERACTION,
  FORM_FIELD_GAP,
  withAlpha,
} from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { toast } from '@/lib/toast'
import { evaluateVehicleIdentity } from '@/lib/utils/vehicle-document-policy'
import type {
  ExtractedInsurancePolicy,
  ExtractedVehicleCard,
  VehicleCardMismatch,
} from '@/lib/types/vehicle-document.types'
import { useEntrance } from '@/lib/hooks/use-entrance'
import { useHostingEligibility } from '@/lib/hooks/use-hosting-eligibility'
import { coverageEligibilityService } from '@/lib/services/coverage-eligibility.service'

/**
 * One height for every dashed dropzone on this step, and one for every
 * captured preview. The front card box was 140 and the back one 100 (previews
 * 180 vs 120) — same dashed border, same centred camera glyph, same caption
 * style, doing the same job at two different sizes.
 */
/** A VIN excludes I, O and Q — the three OCR confuses with 1 and 0. */
const VIN_CHARSET = /^[A-HJ-NPR-Z0-9]+$/

const DOCUMENT_DROPZONE_HEIGHT = 140
const DOCUMENT_PREVIEW_HEIGHT = 180

const DOCUMENT_AI_CONFIDENCE_THRESHOLD = 0.45
const POLICY_AI_MINIMUM_CONFIDENCE = 0.55
const HIDDEN_INSURER_DISPLAY_NAME = 'Aseguradora verificada'
const HIDDEN_INSURER_KEY = 'q' + 'ualitas'
const HIDDEN_INSURER_VISIBLE_PATTERN = /\bqu[áa]litas\b/i
const KNOWN_POLICY_INSURER_PATTERN =
  /\b(qu[a]litas|mapfre|sura|seguros del pacifico|aseguradora agricola|davivienda seguros)\b/
const KNOWN_POLICY_INSURER_NAMES: Record<string, string> = {
  [HIDDEN_INSURER_KEY]: HIDDEN_INSURER_DISPLAY_NAME,
  mapfre: 'MAPFRE',
  sura: 'SURA',
  'seguros del pacifico': 'Seguros del Pacífico',
  'aseguradora agricola': 'Aseguradora Agrícola',
  'davivienda seguros': 'Davivienda Seguros',
}

type DocumentPhotoErrorField = 'frontPhotoUri' | 'backPhotoUri'
type LiveDocumentTarget = 'front' | 'back' | 'policy'
type LiveDocumentEvidence = Pick<LiveCameraCapture, 'uri' | 'capturedAt' | 'source'>

function documentReviewLabelKey(documentType: string): string {
  switch (documentType) {
    case 'tarjeta_circulacion_front':
      return 'listing.document_label_tarjeta_front'
    case 'tarjeta_circulacion_back':
      return 'listing.document_label_tarjeta_back'
    case 'insurance_policy':
    case 'insurance_certificate':
      return 'listing.document_label_insurance_policy'
    default:
      return 'listing.document_label_generic'
  }
}

function DocumentActionRow({ children }: { children: ReactNode }) {
  return (
    <View
      style={{
        flexDirection: 'row',
        gap: SPACING.sm,
        // 8pt read as glued to the box above while whole sections drifted
        // 50-60pt apart. One step out lets the button belong to its dropzone
        // without looking welded to it.
        marginTop: SPACING.md,
      }}
    >
      {children}
    </View>
  )
}

/**
 * Cancels the gap a form control reserves under itself.
 *
 * DatePicker and TextInput both carry `marginBottom: FORM_FIELD_GAP` (32) so
 * screens can stack them without thinking about rhythm. Here every block also
 * declares its own `marginTop`, and the two stacked: 32 + 24 gave 56pt between
 * the Propietario field and the Foto trasera label while the label-to-control
 * gap inside a block was 8. Cancelling the reserved slot leaves this screen
 * with exactly two vertical values — SPACING.xl between sections, SPACING.md
 * inside one.
 */
function SelfSpacedField({ children }: { children: ReactNode }) {
  return <View style={{ marginBottom: -FORM_FIELD_GAP }}>{children}</View>
}

function DocumentActionButton({
  accessibilityLabel,
  colors,
  disabled,
  icon,
  onPress,
  testID,
  title,
  variant,
}: {
  accessibilityLabel: string
  colors: ReturnType<typeof useSemanticColors>
  disabled?: boolean
  icon: ReactNode
  onPress: () => void
  testID: string
  title: string
  variant: 'primary' | 'secondary'
}) {
  const isPrimary = variant === 'primary'
  const contentColor = isPrimary ? '#FFFFFF' : colors.textPrimary
  const resolvedIcon = tintIconNode(icon, contentColor)

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled: Boolean(disabled) }}
      testID={testID}
      style={({ pressed }) => ({
        flex: 1,
        minWidth: 0,
        minHeight: 52,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: SPACING.xs,
        paddingHorizontal: SPACING.sm,
        paddingVertical: SPACING.sm,
        borderRadius: RADIUS.md,
        borderWidth: isPrimary ? 0 : 1,
        borderColor: colors.border,
        backgroundColor: isPrimary ? COLORS.coral.dark : colors.surfaceSubtle,
        opacity: disabled ? 0.5 : pressed ? 0.76 : 1,
      })}
    >
      <View style={{ flexShrink: 0 }}>{resolvedIcon}</View>
      <Text
        style={{
          minWidth: 0,
          flexShrink: 1,
          fontSize: 13,
          lineHeight: 17,
          // 13px — below FONTS.bodyBold's 17px floor. Colour and border already
          // carry the primary/secondary distinction.
          fontFamily: FONTS.body,
          color: contentColor,
          textAlign: 'center',
        }}
        numberOfLines={2}
      >
        {title}
      </Text>
    </Pressable>
  )
}

export default function NewListingDocumentsScreen() {
  const enter = useEntrance()
  const { t } = useTranslation()
  const router = useRouter()
  const { continueEditing, continueLabel } = useListingStepNavigation('review')
  const { e2eJoinOrg, e2eJoinOrgQuery } = useLocalSearchParams<{
    e2eJoinOrg?: string
    e2eJoinOrgQuery?: string
  }>()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const details = useListingWizardStore((s) => s.details)
  const documents = useListingWizardStore((s) => s.documents)
  const policyDeferred = useListingWizardStore((s) => s.policyDeferred)
  const documentReview = useListingWizardStore((s) => s.documentReview)
  const companyDetection = useListingWizardStore((s) => s.companyDetection)
  const ownership = useListingWizardStore((s) => s.ownership)
  const setDocuments = useListingWizardStore((s) => s.setDocuments)
  const setDetails = useListingWizardStore((s) => s.setDetails)
  const setPolicyDeferred = useListingWizardStore((s) => s.setPolicyDeferred)
  const setCompanyDetection = useListingWizardStore((s) => s.setCompanyDetection)
  const setOwnership = useListingWizardStore((s) => s.setOwnership)
  const { currentOrganizationId } = useHostingEligibility()
  // The explicit "whose car is this?" answer (ownership step) is authoritative:
  // a company car is the agency path; "mine" and "someone else's" are the
  // natural-person path where Qualitas is mandatory and no own policy is filed.
  // Company OCR detection is only the legacy fallback for sessions with no answer.
  const ownershipType = ownership?.type ?? null
  const isAgencyListing = ownershipType
    ? ownershipType === 'company'
    : Boolean(
        companyDetection?.organizationId ||
        companyDetection?.userChoice === 'create_org' ||
        companyDetection?.userChoice === 'join_org' ||
        (currentOrganizationId && companyDetection?.userChoice !== 'skip'),
      )
  const { profile } = useProfile()
  // REAL name only. displayName is frequently an email-derived username
  // (zero-friction signup leaves first/last empty — e.g. "emiliojfloresa"), and a
  // username shares no tokens with the tarjeta owner, so falling back to it
  // false-flagged legitimate owners as "el titular no coincide". With no real
  // name to compare, tarjetaOwnerMatchesHost short-circuits to "matches" — this
  // is a soft sanity check, and KYC / Qualitas is the real ownership gate.
  const hostFullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim()
  const [ownerMismatch, setOwnerMismatch] = useState(false)
  // Not identity proof — KYC already did that. Only catches "I said it's mine"
  // when the tarjeta is in a clearly different name. Accent/case-insensitive
  // token overlap, lenient on purpose: a middle or maternal surname still passes.
  const tarjetaOwnerMatchesHost = useCallback(
    (ownerName: string): boolean => {
      const tokens = (value: string) =>
        value
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .toLowerCase()
          .replace(/[^a-z\s]/g, ' ')
          .split(/\s+/)
          .filter((token) => token.length > 1)
      const owner = tokens(ownerName)
      const host = tokens(hostFullName)
      if (owner.length === 0 || host.length === 0) return true
      const shared = host.filter((token) => owner.includes(token)).length
      return shared >= Math.min(2, host.length)
    },
    [hostFullName],
  )
  const makesQuery = useMakes()
  const modelsQuery = useModels(details.makeId)
  const [qualitasEligible, setQualitasEligible] = useState(true)

  useEffect(() => {
    if (!details.category) return
    let active = true
    void coverageEligibilityService
      .isQualitasEligible(details.category, details.year ?? null)
      .then((eligible) => {
        if (active) setQualitasEligible(eligible)
      })
    return () => {
      active = false
    }
  }, [details.category, details.year])

  // Individuals use the platform path only when the category is eligible for
  // it. Categories outside the provider matrix must keep their own-policy
  // evidence instead of silently clearing it.
  useEffect(() => {
    if (isAgencyListing || !qualitasEligible) {
      if (!qualitasEligible) setPolicyDeferred(false)
      return
    }
    setPolicyDeferred(true)
    if (documents.insurancePolicyPhotoUri) {
      setDocuments({
        insurancePolicyPhotoUri: '',
        insurancePolicyPhotoCapturedAt: undefined,
        insurancePolicyPhotoCaptureSource: undefined,
        insurancePolicyFileName: undefined,
        insurancePolicyMimeType: undefined,
        insurancePolicyNumber: '',
        insurancePolicyExpiryDate: '',
        insuranceInsurerName: '',
        insurancePolicyExtractedData: undefined,
        insurancePolicyAiVerified: false,
      })
    }
  }, [
    isAgencyListing,
    qualitasEligible,
    setPolicyDeferred,
    setDocuments,
    documents.insurancePolicyPhotoUri,
  ])

  /**
   * Keep what the tarjeta says about the vehicle itself.
   *
   * The scan already returns make, model, year, colour, fuel type, plate AND
   * the VIN — and this screen used to take four fields off it and drop the
   * rest. The VIN is the expensive loss: `vehicles.vin` expects one, the wizard
   * renders no field for it anywhere, and `compareIdentity` in
   * vehicle-document-policy.ts compares the card's VIN against the listing's,
   * which meant comparing against a value nothing could ever set. A check that
   * can only ever pass.
   *
   * NOTHING THE HOST TYPED IS OVERWRITTEN. Only empty fields are filled. OCR is
   * confident, not right, and silently replacing an answer somebody gave with
   * one a model read off a photo is how a listing ends up describing a
   * different car than its owner does. Where the two disagree, the mismatch
   * machinery is what should speak — quietly, and to a human.
   */
  const adoptCardIntoDetails = useCallback(
    (extracted: ExtractedVehicleCard) => {
      const vin = normalizeVehicleVin(extracted.vin || extracted.chassisNumber)
      const patch: Partial<VehicleDetailsInput> = {}

      // The VIN has no field to conflict with, so it is simply captured — but
      // only at full length. `vehicle.schema.ts` requires exactly 17, so a
      // partial read would be stored as a value the review step then rejects,
      // which is worse than the empty field it replaced.
      // Length AND charset. `vehicle.schema.ts` also enforces /^[A-HJ-NPR-Z0-9]+$/
      // — a real VIN never contains I, O or Q, precisely because OCR confuses
      // them with 1 and 0. Adopting a 17-character read that contains one would
      // have blocked publishing at a field the wizard renders nowhere, so the
      // host would see the listing refused with no way to find the cause.
      if (vin.length === VEHICLE_VIN_LENGTH && VIN_CHARSET.test(vin) && !details.vin) {
        patch.vin = vin
      }
      if (!details.plateNumber && extracted.plateNumber) {
        patch.plateNumber = extracted.plateNumber.toUpperCase()
      }
      if (!details.year && extracted.year) patch.year = extracted.year
      if (!details.color && extracted.color) patch.color = extracted.color

      if (Object.keys(patch).length > 0) setDetails(patch)
    },
    [details.vin, details.plateNumber, details.year, details.color, setDetails],
  )

  const { mutateAsync: extractCard, isPending: isExtracting } = useExtractVehicleCard()
  const { mutateAsync: extractPolicy, isPending: isExtractingPolicy } = useExtractInsurancePolicy()
  const [errors, setErrors] = useState<Record<string, string>>({})
  const scrollRef = useRef<ScrollView>(null)
  const [companySheetVisible, setCompanySheetVisible] = useState(false)
  const [detectedCompanyName, setDetectedCompanyName] = useState('')
  const [showCompanyPrompt, setShowCompanyPrompt] = useState(false)
  const [joinOrgVisible, setJoinOrgVisible] = useState(false)
  const [liveDocumentTarget, setLiveDocumentTarget] = useState<LiveDocumentTarget | null>(null)

  // Route-addressable join state lets users resume the company-join branch
  // and gives E2E a deterministic entry point without camera/OCR flakiness.
  useEffect(() => {
    if (e2eJoinOrg !== '1') return

    const legalName = e2eJoinOrgQuery ?? ''
    setDetectedCompanyName(legalName)
    setCompanyDetection({
      detected: true,
      legalName,
      userChoice: 'join_org',
      organizationId: null,
    })
    setJoinOrgVisible(true)
  }, [e2eJoinOrg, e2eJoinOrgQuery, setCompanyDetection])

  // Cross-validate tarjeta data vs details step
  const mismatches = useMemo(() => {
    const m: VehicleCardMismatch[] = []
    const makeName = makesQuery.data?.find((make) => make.id === details.makeId)?.name
    const modelName = modelsQuery.data?.find((model) => model.id === details.modelId)?.name

    m.push(
      ...buildVehicleCardMismatches({
        extracted: documents.extractedData,
        details,
        makeName,
        modelName,
        sideLabel: t('listing.tarjeta_front_photo'),
        t,
      }),
    )
    m.push(
      ...buildVehicleCardMismatches({
        extracted: documents.backExtractedData,
        details,
        makeName,
        modelName,
        sideLabel: t('listing.tarjeta_back_photo'),
        t,
      }),
    )

    return m
  }, [
    documents.extractedData,
    documents.backExtractedData,
    details,
    makesQuery.data,
    modelsQuery.data,
    t,
  ])

  const clearDocumentError = useCallback((field: DocumentPhotoErrorField) => {
    setErrors((current) => {
      if (!current[field]) return current
      return { ...current, [field]: '' }
    })
  }, [])

  const scrollToDocumentErrors = useCallback(() => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ y: 0, animated: true })
    })
  }, [])

  const processVehicleCardPhoto = useCallback(
    async (photo: LiveDocumentEvidence, side: 'front' | 'back') => {
      // Same durability rule as the 17 vehicle photos: move the capture out of the
      // evictable cache BEFORE it is recorded as attached. Persisting the state
      // while the bytes stayed in cache meant a resumed draft showed the tarjeta
      // as scanned and then failed at publish with a file that no longer existed —
      // a legally required document, silently gone.
      const { capturedAt, source } = photo
      const uri = persistListingCapture(photo.uri)
      void selection()
      clearDocumentError(side === 'front' ? 'frontPhotoUri' : 'backPhotoUri')

      if (side === 'front') {
        setDocuments({
          frontPhotoUri: uri,
          frontPhotoCapturedAt: capturedAt,
          frontPhotoCaptureSource: source,
          tarjetaAiVerified: false,
        })

        // AI extraction
        try {
          const extracted = await extractCard(uri)
          if (extracted.wrongDocument || extracted.confidence < 0.25) {
            const wrongMessage =
              extracted.wrongDocumentMessage ||
              (extracted.documentKind === 'driver_license'
                ? t('listing.tarjeta_wrong_license')
                : extracted.documentKind === 'national_id'
                  ? t('listing.tarjeta_wrong_dui')
                  : null)
            if (wrongMessage) {
              setDocuments({
                frontPhotoUri: '',
                frontPhotoCapturedAt: undefined,
                frontPhotoCaptureSource: undefined,
                tarjetaAiVerified: false,
                extractedData: undefined,
              })
              setErrors((current) => ({
                ...current,
                frontPhotoUri: wrongMessage,
              }))
              toast.error(wrongMessage)
              void notification(NotificationType.Error)
              return
            }
          }

          const tarjetaAiVerified = extracted.confidence >= DOCUMENT_AI_CONFIDENCE_THRESHOLD
          setDocuments({
            documentNumber:
              extracted.tarjetaNumber ??
              extracted.plateNumber ??
              details.plateNumber ??
              documents.documentNumber,
            expiryDate: extracted.expiryDate ?? documents.expiryDate,
            ownerName: extracted.ownerName ?? documents.ownerName,
            extractedData: extracted,
            tarjetaAiVerified,
          })

          adoptCardIntoDetails(extracted)

          // An expired tarjeta must SAY SO, here, at the scan.
          //
          // The circulation permit is refrendado every year, so a lapsed card is
          // common and cheap to fix. But the only thing that used to happen was
          // silence: the document was filed `pending`, the activation gate then
          // refused the vehicle for a reason printed nowhere, and the host — who
          // had already shot all 17 photos — was left with a car that simply
          // never appeared. Even the admin approving it could not see why.
          //
          // Inline on the field, not only a toast: this is the one thing they
          // have to act on, and it must still be on screen after the toast goes.
          const scannedExpiry = extracted.expiryDate?.trim()
          if (scannedExpiry && scannedExpiry < todayISO()) {
            const expiredMessage = t('listing.tarjeta_expired', {
              date: scannedExpiry.split('-').reverse().join('/'),
            })
            setErrors((e) => ({ ...e, expiryDate: expiredMessage }))
            toast.warning(expiredMessage)
          }

          if (!tarjetaAiVerified) {
            toast.info(t('listing.tarjeta_confidence_low'))
          } else {
            void notification(NotificationType.Success)

            const trimmedOwner = extracted.ownerName?.trim()
            if (ownershipType === 'company') {
              // Company car: offer to create / join the organization from the
              // tarjeta's registered legal name.
              if (trimmedOwner) {
                setDetectedCompanyName(trimmedOwner)
                setShowCompanyPrompt(true)
              }
            } else if (
              ownershipType === 'mine' &&
              trimmedOwner &&
              !tarjetaOwnerMatchesHost(trimmedOwner)
            ) {
              // Fraud guard: the host said the car is theirs, but the tarjeta is
              // registered to a clearly different name. Don't hard-block — guide
              // them to the "someone else's" path, which requires authorization.
              setOwnerMismatch(true)
            }
          }
        } catch (err) {
          // Keep the existing toast and capture to Sentry so
          // we see when AI tarjeta extraction is failing (Anthropic
          // 429s, malformed image, edge fn timeout).
          setDocuments({
            tarjetaAiVerified: false,
          })
          toast.error(t('listing.tarjeta_scan_failed'))
          captureError(err, { screen: 'hosting/new-listing/documents', action: 'extract-card' })
        }
      } else {
        setDocuments({
          backPhotoUri: uri,
          backPhotoCapturedAt: capturedAt,
          backPhotoCaptureSource: source,
          tarjetaBackAiVerified: false,
        })

        try {
          const extracted = await extractCard(uri)
          if (extracted.wrongDocument || extracted.confidence < 0.25) {
            const wrongMessage =
              extracted.wrongDocumentMessage ||
              (extracted.documentKind === 'driver_license'
                ? t('listing.tarjeta_wrong_license')
                : extracted.documentKind === 'national_id'
                  ? t('listing.tarjeta_wrong_dui')
                  : null)
            if (wrongMessage) {
              setDocuments({
                backPhotoUri: '',
                backPhotoCapturedAt: undefined,
                backPhotoCaptureSource: undefined,
                tarjetaBackAiVerified: false,
                backExtractedData: undefined,
              })
              setErrors((current) => ({
                ...current,
                backPhotoUri: wrongMessage,
              }))
              toast.error(wrongMessage)
              void notification(NotificationType.Error)
              return
            }
          }

          const tarjetaBackAiVerified = extracted.confidence >= DOCUMENT_AI_CONFIDENCE_THRESHOLD
          setDocuments({
            backExtractedData: extracted,
            tarjetaBackAiVerified,
          })

          // The back of a Salvadoran tarjeta is where the chassis number is
          // printed, so this side is often the only one that yields a VIN. Same
          // rule as the front: fill what is empty, overwrite nothing.
          adoptCardIntoDetails(extracted)

          if (!tarjetaBackAiVerified) {
            toast.info(t('listing.tarjeta_confidence_low'))
          } else {
            void notification(NotificationType.Success)
          }
        } catch (err) {
          setDocuments({ tarjetaBackAiVerified: false })
          toast.error(t('listing.tarjeta_scan_failed'))
          captureError(err, {
            screen: 'hosting/new-listing/documents',
            action: 'extract-card-back',
          })
        }
      }
    },
    [
      details.plateNumber,
      extractCard,
      documents,
      setDocuments,
      adoptCardIntoDetails,
      t,
      clearDocumentError,
      ownershipType,
      tarjetaOwnerMatchesHost,
    ],
  )

  const handleScanDocument = useCallback(
    async (side: 'front' | 'back') => {
      const outcome = await scanDocumentOutcome()
      // A cancel is the host changing their mind — say nothing. A FAILURE is the
      // scanner module refusing to run (outdated Play Services, no wifi to fetch
      // the on-demand ML Kit module), and staying silent there turned the primary
      // button of the last required step into a button that does nothing at all.
      if (outcome.status === 'cancel') return
      if (outcome.status === 'error') {
        captureError(outcome.error, { screen: 'listing_documents', action: 'scan_document' })
        toast.error(t('listing.document_scan_failed'))
        return
      }

      await processVehicleCardPhoto(
        {
          uri: outcome.uri,
          capturedAt: new Date().toISOString(),
          source: 'tago_live_camera',
        },
        side,
      )
    },
    [processVehicleCardPhoto, t],
  )

  const startVehicleCardCapture = useCallback(
    (side: 'front' | 'back') => {
      if (Platform.OS === 'web') {
        setLiveDocumentTarget(side)
        return
      }

      void handleScanDocument(side)
    },
    [handleScanDocument],
  )

  const processPolicyPhoto = useCallback(
    async (photo: LiveDocumentEvidence) => {
      // Durable before it counts as attached — see processVehicleCardPhoto.
      const { capturedAt, source } = photo
      const uri = persistListingCapture(photo.uri)
      void selection()
      setDocuments({
        insurancePolicyPhotoUri: uri,
        insurancePolicyPhotoCapturedAt: capturedAt,
        insurancePolicyPhotoCaptureSource: source,
        insurancePolicyAiVerified: false,
      })
      setPolicyDeferred(false)

      try {
        const extracted = await extractPolicy(uri)
        const normalizedPolicy = normalizeExtractedInsurancePolicy(extracted)
        const approved = isAutoApprovedPolicy(extracted, normalizedPolicy)
        setDocuments({
          insurancePolicyNumber: normalizedPolicy.policyNumber ?? documents.insurancePolicyNumber,
          insurancePolicyExpiryDate:
            normalizedPolicy.expiryDate ?? documents.insurancePolicyExpiryDate,
          insuranceInsurerName: normalizedPolicy.insurerName ?? documents.insuranceInsurerName,
          insurancePolicyExtractedData: extracted,
          insurancePolicyAiVerified: approved,
        })

        if (approved) {
          void notification(NotificationType.Success)
        } else {
          const reason = extracted.rejectionReasons?.[0]
          toast.error(reason ?? t('listing.policy_not_approved'))
        }
      } catch (err) {
        setDocuments({
          insurancePolicyAiVerified: false,
        })
        toast.error(t('listing.policy_scan_failed'))
        captureError(err, { screen: 'hosting/new-listing/documents', action: 'extract-policy' })
      }
    },
    [documents, extractPolicy, setDocuments, setPolicyDeferred, t],
  )

  const captureAndExtractPolicy = useCallback(async () => {
    const outcome = await scanDocumentOutcome()
    if (outcome.status === 'cancel') return
    if (outcome.status === 'error') {
      captureError(outcome.error, { screen: 'listing_documents', action: 'scan_policy' })
      toast.error(t('listing.document_scan_failed'))
      return
    }
    await processPolicyPhoto({
      uri: outcome.uri,
      capturedAt: new Date().toISOString(),
      source: 'tago_live_camera',
    })
  }, [processPolicyPhoto, t])

  const startPolicyCapture = useCallback(() => {
    if (Platform.OS === 'web') {
      setLiveDocumentTarget('policy')
      return
    }

    void captureAndExtractPolicy()
  }, [captureAndExtractPolicy])

  const handleLiveDocumentCapture = useCallback(
    (capture: LiveCameraCapture) => {
      const target = liveDocumentTarget
      setLiveDocumentTarget(null)
      if (!target) return

      if (target === 'policy') {
        void processPolicyPhoto(capture)
        return
      }

      void processVehicleCardPhoto(capture, target)
    },
    [liveDocumentTarget, processPolicyPhoto, processVehicleCardPhoto],
  )

  const handleSkipDocuments = useCallback(() => {
    void selection()
    useListingWizardStore.getState().setDocumentsSkipped(true)
    setPolicyDeferred(qualitasEligible)
    continueEditing()
  }, [qualitasEligible, continueEditing, setPolicyDeferred])

  const handlePolicyModeChange = useCallback(
    (mode: 'upload' | 'deferred') => {
      void selection()
      const deferred = mode === 'deferred'
      if (deferred && !qualitasEligible) {
        setPolicyDeferred(false)
        toast.info(t('listing.policy_non_platform_notice'))
        return
      }
      setPolicyDeferred(deferred)
      if (!deferred) return

      setDocuments({
        insurancePolicyPhotoUri: '',
        insurancePolicyPhotoCapturedAt: undefined,
        insurancePolicyPhotoCaptureSource: undefined,
        insurancePolicyFileName: undefined,
        insurancePolicyMimeType: undefined,
        insurancePolicyNumber: '',
        insurancePolicyExpiryDate: '',
        insuranceInsurerName: '',
        insurancePolicyExtractedData: undefined,
        insurancePolicyAiVerified: false,
      })
    },
    [qualitasEligible, setDocuments, setPolicyDeferred, t],
  )

  const isEditMode = !!useListingWizardStore.getState().vehicleId
  /**
   * En modo edición los documentos son opcionales — "ya los mandaste". Eso vale
   * mientras sigan vigentes. Un documento VENCIDO no es uno ya entregado: es la
   * razón por la que la base rechaza toda reserva del carro
   * (`assert_vehicle_papers_are_current`), y dejar pasar de largo a quien vino
   * justo a renovarlo convierte el aviso en un callejón sin salida.
   */
  // Selector reactivo, no getState(): esto se pinta, y con getState() el banner
  // se quedaría con el valor del primer render aunque el vehículo cargue después.
  const expiredDocs = useMemo(() => documentReview?.expired ?? [], [documentReview?.expired])
  const hasExpiredDocs = isEditMode && expiredDocs.length > 0

  /**
   * ¿Sigue sin reemplazarse el documento QUE VENCIÓ?
   *
   * Preguntar "¿subió algo?" no alcanza: si lo vencido es la póliza y la
   * persona vuelve a capturar la tarjeta, un candado genérico la deja pasar y
   * el carro sigue sin poder recibir reservas — con la persona convencida de
   * que ya lo arregló. El candado tiene que mirar la captura que corresponde a
   * cada tipo vencido.
   */
  const captureForDocumentType = useCallback(
    (documentType: string): string | undefined => {
      switch (documentType) {
        case 'tarjeta_circulacion_front':
          return documents.frontPhotoUri
        case 'tarjeta_circulacion_back':
          return documents.backPhotoUri
        case 'insurance_policy':
        case 'insurance_certificate':
          return documents.insurancePolicyPhotoUri
        default:
          // Un tipo que no conocemos no puede bloquear para siempre a nadie:
          // cualquier captura nueva lo da por atendido. Fallar cerrado acá
          // dejaría al anfitrión encerrado en un paso sin salida.
          return (
            documents.frontPhotoUri ?? documents.backPhotoUri ?? documents.insurancePolicyPhotoUri
          )
      }
    },
    [documents.frontPhotoUri, documents.backPhotoUri, documents.insurancePolicyPhotoUri],
  )

  const stillExpired = useMemo(
    () =>
      hasExpiredDocs ? expiredDocs.filter((doc) => !captureForDocumentType(doc.documentType)) : [],
    [hasExpiredDocs, expiredDocs, captureForDocumentType],
  )

  const handleNext = useCallback(() => {
    if (stillExpired.length > 0) {
      void notification(NotificationType.Warning)
      toast.error(
        t('hosting.expired_doc_must_replace', {
          document: t(documentReviewLabelKey(stillExpired[0]!.documentType)),
        }),
      )
      return
    }
    // In edit mode, documents are optional (already submitted)
    if (isEditMode && !documents.frontPhotoUri && !documents.documentNumber) {
      continueEditing()
      return
    }

    const result = vehicleDocumentSchema.safeParse({
      documentNumber: documents.documentNumber ?? '',
      expiryDate: documents.expiryDate ?? daysFromTodayISO(365),
      ownerName: documents.ownerName ?? '',
      frontPhotoUri: documents.frontPhotoUri ?? (isEditMode ? 'existing' : ''),
      frontPhotoCapturedAt: documents.frontPhotoCapturedAt,
      frontPhotoCaptureSource: documents.frontPhotoCaptureSource,
      backPhotoUri: documents.backPhotoUri ?? (isEditMode ? 'existing' : ''),
      backPhotoCapturedAt: documents.backPhotoCapturedAt,
      backPhotoCaptureSource: documents.backPhotoCaptureSource,
      tarjetaAiVerified: documents.tarjetaAiVerified === true,
      tarjetaBackAiVerified: documents.tarjetaBackAiVerified === true,
      insurancePolicyPhotoUri: documents.insurancePolicyPhotoUri ?? '',
      insurancePolicyPhotoCapturedAt: documents.insurancePolicyPhotoCapturedAt,
      insurancePolicyPhotoCaptureSource: documents.insurancePolicyPhotoCaptureSource,
      insurancePolicyNumber: documents.insurancePolicyNumber ?? '',
      insurancePolicyExpiryDate: documents.insurancePolicyExpiryDate ?? '',
      insuranceInsurerName: documents.insuranceInsurerName ?? '',
      insurancePolicyAiVerified: documents.insurancePolicyAiVerified === true,
    })

    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        const field = issue.path[0] as string
        fieldErrors[field] = t(issue.message)
      }
      setErrors(fieldErrors)
      void notification(NotificationType.Error)
      scrollToDocumentErrors()
      // A validation error can land on a field with NO rendered surface (only
      // expiryDate and ownerName have inline error slots; a documentNumber or a
      // policy-field error has none), which left "Siguiente" permanently dead —
      // it did nothing and the host could never see or clear the failing field.
      // Surface at least one message so the button is never a black hole, and so
      // an off-screen requirement is nameable.
      const RENDERED_ERROR_FIELDS = new Set(['expiryDate', 'ownerName'])
      const offscreen = Object.entries(fieldErrors).find(
        ([field, message]) => Boolean(message) && !RENDERED_ERROR_FIELDS.has(field),
      )
      if (offscreen) toast.error(offscreen[1])
      return
    }

    setDocuments(result.data)
    useListingWizardStore.getState().setDocumentsSkipped(false)
    if (!qualitasEligible && !result.data.insurancePolicyPhotoUri) {
      setPolicyDeferred(false)
      toast.info(t('listing.non_platform_pending_notice'))
    } else if (!isAgencyListing && !result.data.insurancePolicyPhotoUri) {
      setPolicyDeferred(true)
      toast.info(t('listing.individual_qualitas_pending_notice'))
    } else if (!result.data.insurancePolicyPhotoUri) {
      // Agency chose Qualitas coverage instead of uploading their own policy.
      setPolicyDeferred(true)
      toast.info(t('listing.policy_qualitas_agency_notice'))
    } else {
      setPolicyDeferred(false)
    }
    continueEditing()
  }, [
    documents,
    setDocuments,
    continueEditing,
    t,
    isEditMode,
    stillExpired,
    scrollToDocumentErrors,
    setPolicyDeferred,
    isAgencyListing,
    qualitasEligible,
  ])

  const today = todayISO()
  const uploadPolicySelected =
    !qualitasEligible || policyDeferred === false || Boolean(documents.insurancePolicyPhotoUri)
  const rejectedDocuments = documentReview?.rejected ?? []

  return (
    <ScreenContainer variant="content" scroll={false}>
      <KeyboardAwareView
        includeTopInset={false}
        scrollRef={scrollRef}
        contentContainerStyle={{
          padding: SPACING.lg,
          paddingBottom: SPACING['3xl'] + SPACING['2xl'] + SPACING.sm,
        }}
      >
        {/* Document identity callout — people often confuse tarjeta vs licencia. */}
        <Animated.View
          collapsable={false}
          entering={enter(0)}
          testID="documents.identity-callout"
          style={{
            marginBottom: SPACING.lg,
            padding: SPACING.md,
            borderRadius: RADIUS.lg,
            borderWidth: 1,
            borderColor: withAlpha(COLORS.coral.DEFAULT, isDark ? 0.28 : 0.16),
            backgroundColor: withAlpha(COLORS.coral.DEFAULT, isDark ? 0.1 : 0.06),
            gap: SPACING.xs,
          }}
        >
          <Text
            style={{
              fontSize: 15,
              lineHeight: 20,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
            }}
          >
            {t('listing.documents_callout_title')}
          </Text>
          <Text
            style={{
              fontSize: 13,
              lineHeight: 19,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
            }}
          >
            {t('listing.documents_callout_body')}
          </Text>
        </Animated.View>

        {/*
          Vencidos. Es un banner aparte del de rechazados y no una variante del
          mismo, porque dice otra cosa: no "te lo devolvimos, corregilo" sino
          "esto estuvo bien y se venció, y mientras siga así nadie puede
          reservar". Va con la fecha exacta — "vencido" sin fecha manda a la
          persona a buscar el papel para averiguar de qué le estamos hablando.
        */}
        {expiredDocs.length > 0 ? (
          <Animated.View
            collapsable={false}
            entering={enter(1)}
            testID="documents.expired-banner"
            style={{
              marginBottom: SPACING.lg,
              padding: SPACING.md,
              borderRadius: RADIUS.lg,
              borderWidth: 1,
              borderColor: `${COLORS.coral.DEFAULT}55`,
              backgroundColor: isDark ? 'rgba(255,107,90,0.12)' : 'rgba(255,107,90,0.08)',
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: SPACING.sm }}>
              <View
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: RADIUS.full,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: `${COLORS.coral.DEFAULT}18`,
                }}
              >
                <AlertTriangle size={17} color={COLORS.coral.DEFAULT} strokeWidth={2} />
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text
                  style={{
                    fontSize: 15,
                    lineHeight: 20,
                    fontFamily: FONTS.body,
                    color: colors.textPrimary,
                  }}
                >
                  {t('hosting.expired_doc_title')}
                </Text>
                {expiredDocs.map((doc) => (
                  <Text
                    key={doc.id}
                    style={{
                      marginTop: 3,
                      fontSize: 13,
                      lineHeight: 18,
                      fontFamily: FONTS.body,
                      color: colors.textSecondary,
                    }}
                  >
                    {t('hosting.expired_doc_desc', {
                      document: t(documentReviewLabelKey(doc.documentType)),
                      date: doc.expiryDate ? formatSvDateDisplay(doc.expiryDate) : '—',
                    })}
                  </Text>
                ))}
              </View>
            </View>
          </Animated.View>
        ) : null}

        {rejectedDocuments.length > 0 ? (
          <Animated.View
            collapsable={false}
            entering={enter(1)}
            testID="documents.review-banner"
            style={{
              marginBottom: SPACING.lg,
              padding: SPACING.md,
              borderRadius: RADIUS.lg,
              borderWidth: 1,
              borderColor: `${COLORS.coral.DEFAULT}55`,
              backgroundColor: isDark ? 'rgba(255,107,90,0.12)' : 'rgba(255,107,90,0.08)',
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: SPACING.sm }}>
              <View
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: RADIUS.full,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: `${COLORS.coral.DEFAULT}18`,
                }}
              >
                <AlertTriangle size={17} color={COLORS.coral.DEFAULT} strokeWidth={2} />
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text
                  style={{
                    fontSize: 15,
                    lineHeight: 20,
                    fontFamily: FONTS.body,
                    color: colors.textPrimary,
                  }}
                >
                  {t('listing.document_review_required_title')}
                </Text>
                <Text
                  style={{
                    marginTop: 3,
                    fontSize: 13,
                    lineHeight: 18,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                  }}
                >
                  {t('listing.document_review_required_desc')}
                </Text>
              </View>
            </View>

            <View style={{ marginTop: SPACING.sm, gap: SPACING.xs }}>
              {rejectedDocuments.slice(0, 3).map((doc) => (
                <View
                  key={doc.id}
                  style={{
                    padding: SPACING.sm,
                    borderRadius: RADIUS.md,
                    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.76)',
                  }}
                >
                  <Text
                    style={{
                      fontSize: 12,
                      lineHeight: 16,
                      fontFamily: FONTS.body,
                      color: colors.textPrimary,
                    }}
                  >
                    {t(documentReviewLabelKey(doc.documentType))}
                  </Text>
                  <Text
                    style={{
                      marginTop: 2,
                      fontSize: 12,
                      lineHeight: 17,
                      fontFamily: FONTS.body,
                      color: colors.textSecondary,
                    }}
                  >
                    {doc.rejectionReason || t('listing.document_review_reason_fallback')}
                  </Text>
                </View>
              ))}
            </View>

            <Text
              style={{
                marginTop: SPACING.sm,
                fontSize: 12,
                lineHeight: 17,
                fontFamily: FONTS.body,
                color: colors.textMuted,
              }}
            >
              {t('listing.document_review_reupload_note')}
            </Text>
          </Animated.View>
        ) : null}

        {/* Front photo header */}
        <Animated.View collapsable={false} entering={enter(1)}>
          <Text
            style={{
              fontSize: 14,
              fontFamily: FONTS.body,
              color: colors.textPrimary,
              marginBottom: SPACING.sm,
            }}
          >
            {t('listing.tarjeta_front_photo')}
          </Text>

          {/* Photo preview or empty state */}
          {documents.frontPhotoUri ? (
            <View
              style={{
                borderRadius: RADIUS.lg,
                overflow: 'hidden',
                borderWidth: 2,
                borderColor: COLORS.coral.DEFAULT,
              }}
            >
              <ExpoImage
                source={documents.frontPhotoUri}
                style={{ width: '100%', height: DOCUMENT_PREVIEW_HEIGHT }}
                contentFit="cover"
              />
              {isExtracting && (
                <View
                  style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ActivityIndicator size="large" color="#FFFFFF" />
                  <Text
                    style={{
                      fontSize: 14,
                      fontFamily: FONTS.body,
                      color: '#FFFFFF',
                      marginTop: SPACING.sm,
                    }}
                  >
                    {t('listing.tarjeta_scanning')}
                  </Text>
                </View>
              )}
            </View>
          ) : (
            <View
              style={{
                height: DOCUMENT_DROPZONE_HEIGHT,
                borderRadius: RADIUS.lg,
                borderWidth: 2,
                borderColor: colors.border,
                borderStyle: 'dashed',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDark ? colors.surface : colors.surfaceSubtle,
              }}
            >
              <Camera size={32} color={colors.textMuted} strokeWidth={1.5} />
              <Text
                style={{
                  fontSize: 13,
                  fontFamily: FONTS.body,
                  color: colors.textMuted,
                  marginTop: SPACING.sm,
                }}
              >
                {t('listing.tarjeta_circulacion')}
              </Text>
            </View>
          )}

          {/* Scan button — deliberately secondary. Filled coral is reserved
              for "Siguiente" in the footer: two identical full-width coral
              buttons on one screen meant neither led. */}
          <DocumentActionRow>
            <DocumentActionButton
              onPress={() => {
                startVehicleCardCapture('front')
              }}
              disabled={isExtracting}
              accessibilityLabel={t('listing.scan_document')}
              testID="documents.front-scan"
              title={t('listing.scan_document')}
              icon={<Scan size={16} />}
              variant="secondary"
              colors={colors}
            />
          </DocumentActionRow>
          {errors.frontPhotoUri ? (
            <Text
              accessibilityRole="alert"
              testID="documents.front-error"
              style={{
                marginTop: SPACING.xs,
                fontSize: 12,
                lineHeight: 17,
                fontFamily: FONTS.body,
                color: colors.textError,
              }}
            >
              {errors.frontPhotoUri}
            </Text>
          ) : null}
        </Animated.View>

        {/* Mismatch warning */}
        {mismatches.length > 0 && (
          <Animated.View
            collapsable={false}
            entering={enter(2)}
            style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: SPACING.sm,
              padding: SPACING.md,
              marginTop: SPACING.md,
              backgroundColor: `${colors.warning}15`,
              borderRadius: RADIUS.md,
              borderLeftWidth: 3,
              borderLeftColor: colors.warning,
            }}
          >
            <AlertTriangle size={18} color={colors.warning} />
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 13, fontFamily: FONTS.body, color: colors.warning }}>
                {t('listing.tarjeta_mismatch_warning')}
              </Text>
              {mismatches.map((m) => (
                <Text
                  key={m.field}
                  style={{
                    fontSize: 12,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                    marginTop: 2,
                  }}
                >
                  {m.field}: {m.wizardValue} → {m.tarjetaValue}
                </Text>
              ))}
            </View>
          </Animated.View>
        )}

        {/* Extracted fields */}
        <Animated.View collapsable={false} entering={enter(4)} style={{ marginTop: SPACING.xl }}>
          <SelfSpacedField>
            <DatePicker
              label={t('listing.tarjeta_expiry')}
              value={documents.expiryDate ?? null}
              onChange={(d) => {
                setDocuments({ expiryDate: d, tarjetaAiVerified: false })
                setErrors((e) => ({ ...e, expiryDate: '' }))
              }}
              minDate={today}
              mode="month"
              error={errors.expiryDate}
            />
          </SelfSpacedField>
        </Animated.View>

        <Animated.View collapsable={false} entering={enter(5)} style={{ marginTop: SPACING.xl }}>
          <SelfSpacedField>
            <TextInput
              label={t('listing.tarjeta_owner')}
              value={documents.ownerName ?? ''}
              onChangeText={(v) => {
                setDocuments({ ownerName: v, tarjetaAiVerified: false })
                setErrors((e) => ({ ...e, ownerName: '' }))
              }}
              placeholder={t('listing.placeholder_owner_name')}
              error={errors.ownerName}
              testID="documents.owner-name"
            />
          </SelfSpacedField>
        </Animated.View>

        {/* Confidence indicator — the chip used to print the *failure* sentence
            ("could not read all fields with enough confidence") next to a green
            check on a green background whenever the read had succeeded, because
            it reused the low-confidence string for both outcomes. Sentence,
            icon and tint now all say the same thing. */}
        {(() => {
          const extracted = documents.extractedData
          if (!extracted) return null

          const confident = extracted.confidence >= DOCUMENT_AI_CONFIDENCE_THRESHOLD
          const tone = confident ? colors.success : colors.warning
          const ConfidenceIcon = confident ? CheckCircle : AlertTriangle

          return (
            <Animated.View
              collapsable={false}
              entering={enter(6)}
              testID="documents.confidence-chip"
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: SPACING.sm,
                marginTop: SPACING.md,
                padding: SPACING.sm,
                borderRadius: RADIUS.sm,
                backgroundColor: `${tone}12`,
              }}
            >
              <ConfidenceIcon size={16} color={tone} />
              <Text
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 12,
                  lineHeight: 17,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                }}
              >
                {t(
                  confident
                    ? 'listing.tarjeta_confidence_ok'
                    : 'listing.tarjeta_confidence_warning',
                  { percent: Math.round(extracted.confidence * 100) },
                )}
              </Text>
            </Animated.View>
          )
        })()}

        {/* Ownership mismatch — host said "it's mine" but the tarjeta is in
            another name. Guide, don't block: offer the "someone else's" path. */}
        {ownerMismatch ? (
          <Animated.View
            collapsable={false}
            entering={enter(7)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.sm,
              marginTop: SPACING.md,
              padding: SPACING.md,
              borderRadius: RADIUS.md,
              backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(45,42,94,0.04)',
              borderWidth: 1,
              borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(45,42,94,0.08)',
            }}
          >
            <AlertTriangle size={18} color={colors.textSecondary} strokeWidth={1.5} />
            <Text
              style={{
                flex: 1,
                fontSize: 13,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
                lineHeight: 20,
              }}
            >
              {t('listing.ownership_mismatch_body')}
            </Text>
            <Pressable
              onPress={() => {
                void selection()
                setOwnerMismatch(false)
                setOwnership({
                  type: 'third_party',
                  authorizedByName: documents.ownerName?.trim() || undefined,
                })
                toast.info(t('listing.ownership_switched_third_party'))
              }}
              accessibilityRole="button"
              accessibilityLabel={t('listing.ownership_mismatch_switch')}
              testID="documents.switch-third-party"
              style={{
                paddingHorizontal: SPACING.md,
                paddingVertical: SPACING.xs + 2,
                // 44pt: the fraud-guard escape hatch was ~29pt and untestable by
                // Maestro. A host listing someone else's car must be able to say
                // so on the first tap.
                minHeight: 44,
                justifyContent: 'center',
                borderRadius: RADIUS.sm,
                backgroundColor: COLORS.coral.dark,
              }}
            >
              <Text style={{ fontSize: 13, fontFamily: FONTS.body, color: '#FFFFFF' }}>
                {t('listing.ownership_mismatch_switch')}
              </Text>
            </Pressable>
          </Animated.View>
        ) : null}

        {/* Subtle company prompt — shown when regex didn't auto-detect */}
        {showCompanyPrompt && !companySheetVisible && (
          <Animated.View
            collapsable={false}
            entering={enter(7)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.sm,
              marginTop: SPACING.md,
              padding: SPACING.md,
              borderRadius: RADIUS.md,
              backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(45,42,94,0.04)',
              borderWidth: 1,
              borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(45,42,94,0.08)',
            }}
          >
            <Building2 size={18} color={colors.textSecondary} strokeWidth={1.5} />
            <Text
              style={{
                flex: 1,
                fontSize: 13,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
                lineHeight: 20,
              }}
            >
              {t('org.company_prompt')}
            </Text>
            <Pressable
              onPress={() => {
                void selection()
                setShowCompanyPrompt(false)
                setCompanySheetVisible(true)
              }}
              accessibilityRole="button"
              accessibilityLabel={t('common.yes')}
              testID="documents.company-confirm"
              style={{
                paddingHorizontal: SPACING.md,
                paddingVertical: SPACING.xs + 2,
                // 44pt: this opens the create/join-organization sheet, and it was
                // ~29pt — a mis-tap on it silently drops an agency host into the
                // individual path.
                minHeight: 44,
                justifyContent: 'center',
                borderRadius: RADIUS.sm,
                backgroundColor: COLORS.coral.dark,
              }}
            >
              <Text style={{ fontSize: 13, fontFamily: FONTS.body, color: '#FFFFFF' }}>
                {t('common.yes')}
              </Text>
            </Pressable>
          </Animated.View>
        )}

        {/* Back photo */}
        <Animated.View collapsable={false} entering={enter(7)} style={{ marginTop: SPACING.xl }}>
          <Text
            style={{
              fontSize: 14,
              fontFamily: FONTS.body,
              color: colors.textPrimary,
              marginBottom: SPACING.sm,
            }}
          >
            {t('listing.tarjeta_back_photo')}
          </Text>

          {documents.backPhotoUri ? (
            <View
              style={{
                borderRadius: RADIUS.lg,
                overflow: 'hidden',
                borderWidth: 2,
                borderColor: COLORS.coral.DEFAULT,
              }}
            >
              <ExpoImage
                source={documents.backPhotoUri}
                style={{ width: '100%', height: DOCUMENT_PREVIEW_HEIGHT }}
                contentFit="cover"
              />
            </View>
          ) : (
            <View
              style={{
                height: DOCUMENT_DROPZONE_HEIGHT,
                borderRadius: RADIUS.lg,
                borderWidth: 2,
                borderColor: colors.border,
                borderStyle: 'dashed',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDark ? colors.surface : colors.surfaceSubtle,
              }}
            >
              <Camera size={32} color={colors.textMuted} strokeWidth={1.5} />
              <Text
                style={{
                  fontSize: 13,
                  fontFamily: FONTS.body,
                  color: colors.textMuted,
                  marginTop: SPACING.sm,
                }}
              >
                {t('listing.tarjeta_back_photo')}
              </Text>
            </View>
          )}

          <DocumentActionRow>
            <DocumentActionButton
              onPress={() => {
                startVehicleCardCapture('back')
              }}
              disabled={isExtracting}
              accessibilityLabel={t('listing.scan_document')}
              testID="documents.back-scan"
              title={t('listing.scan_document')}
              icon={<Scan size={16} />}
              variant="secondary"
              colors={colors}
            />
          </DocumentActionRow>
          {errors.backPhotoUri ? (
            <Text
              accessibilityRole="alert"
              testID="documents.back-error"
              style={{
                marginTop: SPACING.xs,
                fontSize: 12,
                lineHeight: 17,
                fontFamily: FONTS.body,
                color: colors.textAccent,
              }}
            >
              {errors.backPhotoUri}
            </Text>
          ) : null}
        </Animated.View>

        {/* Insurance: Qualitas by default, or their own rental policy if they have one. */}
        <Animated.View
          collapsable={false}
          entering={enter(8)}
          style={{ marginTop: SPACING.xl }}
          testID="documents.agency-policy-path"
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
            <ShieldCheck size={18} color={COLORS.coral.DEFAULT} strokeWidth={2} />
            <Text
              style={{
                fontSize: 14,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
              }}
            >
              {t('listing.policy_photo')}
            </Text>
          </View>
          <Text
            style={{
              marginTop: SPACING.xs,
              fontSize: 13,
              lineHeight: 20,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
            }}
          >
            {qualitasEligible
              ? t('listing.policy_agency_choice_hint')
              : t('listing.policy_non_platform_notice')}
          </Text>
          {qualitasEligible ? (
            <View style={{ marginTop: SPACING.md }}>
              <InsurerBadge variant="horizontal" testID="documents.coverage-provider-badge" />
            </View>
          ) : null}

          <View
            style={{
              flexDirection: 'row',
              gap: SPACING.sm,
              marginTop: SPACING.md,
            }}
          >
            <Pressable
              onPress={() => handlePolicyModeChange('upload')}
              accessibilityRole="button"
              accessibilityState={{ selected: uploadPolicySelected }}
              testID="documents.policy-mode-upload"
              style={{
                flex: 1,
                minHeight: 52,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: SPACING.xs,
                paddingHorizontal: SPACING.sm,
                borderRadius: RADIUS.md,
                borderWidth: 1,
                borderColor: uploadPolicySelected ? COLORS.coral.DEFAULT : colors.border,
                backgroundColor: uploadPolicySelected
                  ? `${COLORS.coral.DEFAULT}12`
                  : colors.surface,
              }}
            >
              <ShieldCheck
                size={16}
                color={uploadPolicySelected ? COLORS.coral.DEFAULT : colors.textSecondary}
              />
              <Text
                style={{
                  minWidth: 0,
                  flexShrink: 1,
                  fontSize: 13,
                  lineHeight: 17,
                  fontFamily: FONTS.body,
                  color: uploadPolicySelected ? colors.textAccent : colors.textSecondary,
                  textAlign: 'center',
                }}
                numberOfLines={2}
              >
                {t('listing.policy_own')}
              </Text>
            </Pressable>

            {qualitasEligible ? (
              <Pressable
                onPress={() => handlePolicyModeChange('deferred')}
                accessibilityRole="button"
                accessibilityState={{ selected: !uploadPolicySelected }}
                testID="documents.policy-mode-deferred"
                style={{
                  flex: 1,
                  minHeight: 52,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: SPACING.xs,
                  paddingHorizontal: SPACING.sm,
                  borderRadius: RADIUS.md,
                  borderWidth: 1,
                  borderColor: !uploadPolicySelected ? COLORS.coral.DEFAULT : colors.border,
                  backgroundColor: !uploadPolicySelected
                    ? `${COLORS.coral.DEFAULT}12`
                    : colors.surface,
                }}
              >
                <ShieldCheck
                  size={16}
                  color={!uploadPolicySelected ? COLORS.coral.DEFAULT : colors.textSecondary}
                />
                <Text
                  style={{
                    minWidth: 0,
                    flexShrink: 1,
                    fontSize: 13,
                    lineHeight: 17,
                    fontFamily: FONTS.body,
                    color: !uploadPolicySelected ? colors.textAccent : colors.textSecondary,
                    textAlign: 'center',
                  }}
                  numberOfLines={2}
                >
                  {t('listing.policy_qualitas')}
                </Text>
              </Pressable>
            ) : null}
          </View>

          {uploadPolicySelected ? (
            documents.insurancePolicyPhotoUri ? (
              <View
                style={{
                  marginTop: SPACING.sm,
                  borderRadius: RADIUS.lg,
                  overflow: 'hidden',
                  borderWidth: 2,
                  borderColor: documents.insurancePolicyAiVerified
                    ? colors.success
                    : COLORS.coral.DEFAULT,
                }}
              >
                <ExpoImage
                  source={documents.insurancePolicyPhotoUri}
                  style={{ width: '100%', height: DOCUMENT_PREVIEW_HEIGHT }}
                  contentFit="cover"
                />
                {isExtractingPolicy && (
                  <View
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ActivityIndicator size="large" color="#FFFFFF" />
                    <Text
                      style={{
                        fontSize: 14,
                        fontFamily: FONTS.body,
                        color: '#FFFFFF',
                        marginTop: SPACING.sm,
                      }}
                    >
                      {t('listing.policy_scanning')}
                    </Text>
                  </View>
                )}
              </View>
            ) : (
              <View
                style={{
                  height: DOCUMENT_DROPZONE_HEIGHT,
                  marginTop: SPACING.sm,
                  borderRadius: RADIUS.lg,
                  borderWidth: 2,
                  borderColor: colors.border,
                  borderStyle: 'dashed',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDark ? colors.surface : colors.surfaceSubtle,
                }}
              >
                <FileText size={30} color={colors.textMuted} strokeWidth={1.5} />
                <Text
                  style={{
                    fontSize: 13,
                    fontFamily: FONTS.body,
                    color: colors.textMuted,
                    marginTop: SPACING.sm,
                  }}
                >
                  {t('listing.policy_document')}
                </Text>
              </View>
            )
          ) : (
            <View
              style={{
                marginTop: SPACING.md,
                padding: SPACING.md,
                borderRadius: RADIUS.lg,
                borderWidth: 1,
                borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(45,42,94,0.08)',
                backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(45,42,94,0.04)',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: SPACING.sm,
              }}
            >
              <ShieldCheck size={18} color={COLORS.coral.DEFAULT} strokeWidth={2} />
              <Text
                style={{
                  flex: 1,
                  fontSize: 13,
                  lineHeight: 19,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                }}
              >
                {qualitasEligible
                  ? t('listing.policy_qualitas_agency_notice')
                  : t('listing.policy_non_platform_notice')}
              </Text>
            </View>
          )}

          {uploadPolicySelected ? (
            <DocumentActionRow>
              <DocumentActionButton
                onPress={() => {
                  startPolicyCapture()
                }}
                disabled={isExtractingPolicy}
                accessibilityLabel={t('listing.scan_document')}
                testID="documents.policy-scan"
                title={t('listing.scan_document')}
                icon={<Scan size={16} />}
                variant="secondary"
                colors={colors}
              />
            </DocumentActionRow>
          ) : null}

          {uploadPolicySelected && documents.insurancePolicyExtractedData ? (
            <View
              style={{
                marginTop: SPACING.md,
                padding: SPACING.md,
                borderRadius: RADIUS.md,
                backgroundColor: documents.insurancePolicyAiVerified
                  ? `${colors.success}12`
                  : `${colors.warning}12`,
                gap: SPACING.xs,
              }}
            >
              <Text
                style={{
                  fontSize: 13,
                  fontFamily: FONTS.body,
                  color: documents.insurancePolicyAiVerified ? colors.success : colors.warning,
                }}
              >
                {documents.insurancePolicyAiVerified
                  ? t('listing.policy_ai_approved')
                  : t('listing.policy_ai_needs_review')}
              </Text>
              <Text
                style={{
                  fontSize: 12,
                  lineHeight: 18,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                }}
              >
                {documents.insuranceInsurerName || t('listing.policy_unknown_insurer')} ·{' '}
                {documents.insurancePolicyNumber || t('listing.policy_unknown_number')} ·{' '}
                {documents.insurancePolicyExpiryDate || t('listing.policy_unknown_expiry')}
              </Text>
            </View>
          ) : null}
        </Animated.View>

        {/* Skip for now */}
        {!isEditMode && (
          <Animated.View
            collapsable={false}
            entering={enter(8)}
            style={{ marginTop: SPACING.xl, alignItems: 'center' }}
          >
            <Pressable
              onPress={handleSkipDocuments}
              accessibilityRole="button"
              accessibilityLabel={t('listing.skip_documents')}
              testID="documents.skip-button"
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: SPACING.xs,
                minHeight: INTERACTION.minTouchTarget,
                paddingVertical: SPACING.sm,
              }}
            >
              <SkipForward size={16} color={colors.textMuted} />
              <Text style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textMuted }}>
                {t('listing.skip_documents')}
              </Text>
            </Pressable>
            <Text
              style={{
                fontSize: 12,
                fontFamily: FONTS.body,
                color: colors.textMuted,
                textAlign: 'center',
                marginTop: SPACING.xs,
              }}
            >
              {t('listing.skip_documents_hint')}
            </Text>
          </Animated.View>
        )}
      </KeyboardAwareView>

      <FormActionBar style={{ backgroundColor: colors.background }}>
        <Button
          onPress={handleNext}
          variant="primary"
          size="lg"
          title={continueLabel}
          disabled={isExtracting || isExtractingPolicy}
          testID="documents.next-button"
        />
      </FormActionBar>
      <LiveCameraModal
        visible={liveDocumentTarget !== null}
        title={
          liveDocumentTarget === 'front'
            ? t('listing.tarjeta_front_photo')
            : liveDocumentTarget === 'back'
              ? t('listing.tarjeta_back_photo')
              : t('listing.policy_photo')
        }
        helper={t('listing.live_document_helper')}
        frameAspectRatio={1.6}
        onCancel={() => setLiveDocumentTarget(null)}
        onCapture={handleLiveDocumentCapture}
      />
      {/* Company Detection Sheet */}
      <CompanyDetectedSheet
        visible={companySheetVisible}
        companyName={detectedCompanyName}
        onCreateOrg={() => {
          setCompanySheetVisible(false)
          setCompanyDetection({
            detected: true,
            legalName: detectedCompanyName,
            userChoice: 'create_org',
            organizationId: null,
          })
          router.push(
            `/hosting/organization/setup?legalName=${encodeURIComponent(detectedCompanyName)}` as never,
          )
        }}
        onJoinOrg={() => {
          setCompanySheetVisible(false)
          setCompanyDetection({
            detected: true,
            legalName: detectedCompanyName,
            userChoice: 'join_org',
            organizationId: null,
          })
          setJoinOrgVisible(true)
        }}
        onSkip={() => {
          setCompanySheetVisible(false)
          setCompanyDetection({
            detected: true,
            legalName: detectedCompanyName,
            userChoice: 'skip',
            organizationId: null,
          })
        }}
        onClose={() => setCompanySheetVisible(false)}
      />
      <JoinOrgSheet
        visible={joinOrgVisible}
        onClose={() => setJoinOrgVisible(false)}
        initialSearchQuery={detectedCompanyName}
        onJoined={(orgId) => {
          setCompanyDetection({
            detected: true,
            legalName: detectedCompanyName,
            userChoice: 'join_org',
            organizationId: orgId,
          })
        }}
      />
    </ScreenContainer>
  )
}

function normalizeExtractedInsurancePolicy(extracted: ExtractedInsurancePolicy): {
  policyNumber: string | null
  expiryDate: string | null
  insurerName: string | null
} {
  return {
    policyNumber: extracted.policyNumber?.trim() || extractPolicyNumberFromEvidence(extracted),
    expiryDate: extracted.expiryDate?.trim() || extractLatestPolicyDateFromEvidence(extracted),
    insurerName: sanitizeVisibleInsurerName(
      extracted.insurerName?.trim() || extractPolicyInsurerFromEvidence(extracted),
    ),
  }
}

function sanitizeVisibleInsurerName(name: string | null): string | null {
  if (!name) return null
  if (HIDDEN_INSURER_VISIBLE_PATTERN.test(name)) return HIDDEN_INSURER_DISPLAY_NAME
  return name
}

function isAutoApprovedPolicy(
  extracted: ExtractedInsurancePolicy,
  normalized: ReturnType<typeof normalizeExtractedInsurancePolicy>,
): boolean {
  const evidence = normalizedPolicyEvidenceText(extracted)
  const isPolicyLike =
    extracted.documentKind === 'insurance_policy' ||
    extracted.documentKind === 'insurance_certificate' ||
    /\b(poliza|certificado de seguro|seguro de automoviles|aseguradora|compania de seguros)\b/.test(
      evidence,
    )
  const hasLiability =
    extracted.hasThirdPartyLiability === true ||
    /\b(responsabilidad civil|respons civil|danos a terceros|civil bienes|civil personas)\b/.test(
      evidence,
    )
  const hasVehicleIdentity =
    Boolean(extracted.plateNumber?.trim() || extracted.vin?.trim()) ||
    /\b(placa|placas|vin|chasis|chassis)\s*:?\s*[a-z0-9-]{4,17}\b/.test(evidence)
  const hasFutureExpiry = Boolean(normalized.expiryDate && normalized.expiryDate > todayISO())
  const rentalUseAllowed =
    extracted.rentalUseExcluded !== true ||
    /\b(uso\s*:?\s*renta diaria|servicio\s*:?\s*renta diaria|rent a car)\b/.test(evidence)

  return Boolean(
    isPolicyLike &&
    normalized.policyNumber &&
    normalized.insurerName &&
    hasVehicleIdentity &&
    hasFutureExpiry &&
    hasLiability &&
    rentalUseAllowed &&
    (extracted.recommendation === 'approved' ||
      extracted.confidence >= POLICY_AI_MINIMUM_CONFIDENCE),
  )
}

function extractPolicyNumberFromEvidence(extracted: ExtractedInsurancePolicy): string | null {
  const text = normalizedPolicyEvidenceText(extracted).toUpperCase()
  return (
    text.match(/\bPOLIZA\s*(?:N(?:O|UMERO)?\.?|#|:)?\s*([A-Z0-9][A-Z0-9-]{3,})\b/)?.[1] ??
    text.match(/\bCERTIFICADO\s*(?:N(?:O|UMERO)?\.?|#|:)?\s*([A-Z0-9][A-Z0-9-]{3,})\b/)?.[1] ??
    null
  )
}

function extractLatestPolicyDateFromEvidence(extracted: ExtractedInsurancePolicy): string | null {
  const text = normalizedPolicyEvidenceText(extracted)
  const isoDates = Array.from(
    text.matchAll(/\b(20\d{2})[-/.](0?[1-9]|1[0-2])[-/.](0?[1-9]|[12]\d|3[01])\b/g),
  )
    .map((match) => {
      const [, yearValue, monthValue, dayValue] = match
      if (!yearValue || !monthValue || !dayValue) return ''
      return `${yearValue}-${monthValue.padStart(2, '0')}-${dayValue.padStart(2, '0')}`
    })
    .filter((value): value is string => Boolean(value))
  const looseDates = Array.from(
    text.matchAll(/\b(\d{1,2})[-/. ]([a-z0-9]{1,10})[-/. ](\d{2,4})\b/g),
  )
    .map((match) => parseLoosePolicyDate(match[1] ?? '', match[2] ?? '', match[3] ?? ''))
    .filter((value): value is string => Boolean(value))
  return [...isoDates, ...looseDates].sort().at(-1) ?? null
}

function extractPolicyInsurerFromEvidence(extracted: ExtractedInsurancePolicy): string | null {
  const rawText = [extracted.rawText, extracted.coverageSummary].filter(Boolean).join('\n')
  const knownInsurer = normalizedPolicyEvidenceText(extracted).match(
    KNOWN_POLICY_INSURER_PATTERN,
  )?.[1]
  if (knownInsurer) return KNOWN_POLICY_INSURER_NAMES[knownInsurer] ?? knownInsurer

  return (
    rawText
      .split(/\r?\n/)
      .map((value) => value.trim())
      .find((value) => /seguros|aseguradora|compa[ñn]ia/i.test(value)) ?? null
  )
}

function normalizedPolicyEvidenceText(extracted: ExtractedInsurancePolicy): string {
  return [extracted.rawText, extracted.coverageSummary]
    .filter(Boolean)
    .join('\n')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' y ')
    .replace(/[^a-z0-9#:/.\-\s]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function parseLoosePolicyDate(
  dayValue: string,
  monthValue: string,
  yearValue: string,
): string | null {
  const day = Number(dayValue)
  const month = parseLoosePolicyMonth(monthValue)
  const year = Number(yearValue.length === 2 ? `20${yearValue}` : yearValue)
  if (!day || !month || !year || day > 31) return null
  return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day
    .toString()
    .padStart(2, '0')}`
}

function parseLoosePolicyMonth(value: string): number {
  const numeric = Number(value)
  if (Number.isInteger(numeric) && numeric >= 1 && numeric <= 12) return numeric

  const months: Record<string, number> = {
    ene: 1,
    enero: 1,
    jan: 1,
    january: 1,
    feb: 2,
    febrero: 2,
    february: 2,
    mar: 3,
    marzo: 3,
    march: 3,
    abr: 4,
    abril: 4,
    apr: 4,
    april: 4,
    may: 5,
    mayo: 5,
    jun: 6,
    junio: 6,
    june: 6,
    jul: 7,
    julio: 7,
    july: 7,
    ago: 8,
    agosto: 8,
    aug: 8,
    august: 8,
    sep: 9,
    set: 9,
    septiembre: 9,
    september: 9,
    oct: 10,
    octubre: 10,
    october: 10,
    nov: 11,
    noviembre: 11,
    november: 11,
    dic: 12,
    diciembre: 12,
    dec: 12,
    december: 12,
  }
  return months[value] ?? 0
}

function buildVehicleCardMismatches({
  extracted,
  details,
  makeName,
  modelName,
  sideLabel,
  t,
}: {
  extracted: ExtractedVehicleCard | undefined
  details: Partial<VehicleDetailsInput>
  makeName: string | undefined
  modelName: string | undefined
  sideLabel: string
  t: (key: string) => string
}): VehicleCardMismatch[] {
  if (!extracted) return []

  const prefix = (label: string) => `${sideLabel} · ${label}`
  const labels = {
    plate: t('listing.plate_number'),
    vin: 'VIN',
    make: t('listing.make'),
    model: t('listing.model'),
    color: t('listing.color'),
    category: t('listing.category'),
  }

  return evaluateVehicleIdentity(extracted, {
    plateNumber: details.plateNumber,
    vin: details.vin,
    make: makeName,
    model: modelName,
    color: details.color,
    category: details.category,
  }).mismatches.map((mismatch) => ({
    field: prefix(labels[mismatch.field]),
    wizardValue: mismatch.expected,
    tarjetaValue: mismatch.actual,
  }))
}
